import { gql } from '@apollo/client/core';
import { Page, StyleSheet } from '@react-pdf/renderer';
import gtAmerica from './fonts/gtAmerica';
import React from 'react';
import {
  Box,
  Flex,
  QRCode,
  Registration,
  SemiTitle,
  Text,
} from '../../pdf-design-system';
import { ThemeProvider } from '../../pdf-design-system/useTheme';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import messages from './locale/messages';
import MaximeHerkunftTableSignHeader from './MaximeHerkunftTableSignHeader';
import SITE_MAPPING from './SITE_MAPPING';

/**
 * The Layout component is responsible for rendering one or multiple pages.
 * It sets the page size and sets up the wrapping.
 * In this case we have multiple items per page that might wrap, so we add a single page only.
 * It renders the page header and footer, and a list of sections in between.
 * A closing spacer ensures that the footer is on the page bottom,
 * without resorting to absolute positioning.
 * The stencil will be placed correctly if we use `padding` for the page margins.
 */
export default function MaximeHerkunftTableSign({
  data,
  showStencil,
  collectionId,
  exhibitorId,
  locale,
  ...rest
}) {
  const registration = afterQuery(data);

  return (
    <ThemeProvider theme={MaximeHerkunftTableSign.theme}>
      <UserLocaleProvider locale={locale} messages={messages}>
        <Page size="A4" key={registration.registrationId} {...rest}>
          {/* ISSUE: Stencil is not rendering full height */}
          {/* {showStencil && <Stencil src={stencil} fixed />} */}
          <Registration.Provider value={registration}>
            <MaximeHerkunftTableSignHeader style={{ marginTop: '2cm' }} />
            <Box style={{ marginTop: 33, flexGrow: 1 }}>
              <Flex
                style={{
                  alignItems: 'center',
                  flexDirection: 'column',
                  textAlign: 'center',
                  marginTop: '2cm',
                }}
              >
                {registration?.stand?.name && (
                  <SemiTitle style={{ fontSize: 36 }}>
                    {registration?.stand?.name}
                  </SemiTitle>
                )}
                <SemiTitle>{registration?.profile?.name || ' '}</SemiTitle>
              </Flex>
            </Box>
            <Flex
              style={{
                width: '100%',
                paddingBottom: `2cm`,
                bottom: 0,
                left: 0,
                right: 0,
                justifyContent: 'center',
              }}
              {...rest}
              fixed
            >
              <Flex direction="column" style={{ alignItems: 'center' }}>
                <QRCode
                  url={`${SITE_MAPPING[collectionId]}/e/${registration.companyId}`}
                  errorCorrectionLevel="L"
                  margin={0}
                  style={{
                    width: '2in',
                    height: '2in',
                    flexShrink: 0,
                  }}
                />
                <Text style={{ fontSize: 24, marginTop: 8 }}>
                  Scannen für persönliche Notizen
                </Text>
              </Flex>
            </Flex>
          </Registration.Provider>
        </Page>
      </UserLocaleProvider>
    </ThemeProvider>
  );
}

MaximeHerkunftTableSign.fragment = gql`
  fragment MaximeHerkunftTableSign on CatalogSection {
    sectionType
    ... on RegistrationCatalogSection {
      registration {
        registrationId
        companyId
        profile {
          profileId
          name
        }
        stand {
          name
        }
        byBrand: registeredProducts {
          brands: group(field: producer___name, sort: { field: fieldValue }) {
            name: fieldValue
            nodes(first: 1) {
              producer {
                profile {
                  stateProvince
                }
              }
            }
          }
        }
      }
    }
  }
`;

function afterQuery({ registration }) {
  const brands = registration.byBrand?.brands;

  const enrichedBrands =
    brands?.length >= 2
      ? // If there is more than one brand, include them all.
        brands
      : // Otherwise flag if they are the same
        brands.map((brand) => ({
          ...brand,
          isSameAsExhibitor: brand.name === registration.profile.name,
        }));

  const groupByRegion = enrichedBrands
    ?.reduce((acc, brand) => {
      const stateProvince = brand.nodes?.[0]?.producer?.profile?.stateProvince;
      const byState = acc.find((state) => {
        return state.stateProvince === stateProvince;
      });
      const normalizedBrandObject = { ...brand, stateProvince };
      if (byState) {
        byState.brands.push(normalizedBrandObject);
        return acc;
      }
      acc.push({
        stateProvince,
        brands: [normalizedBrandObject],
      });
      return acc;
    }, [])
    .sort((a, b) => (a.stateProvince > b.stateProvince ? 1 : -1));
  return { ...registration, groupByRegion };
}

MaximeHerkunftTableSign.theme = StyleSheet.create({
  page: {},
  headerNormal: {
    ...gtAmerica,
    fontWeight: 400,
    fontSize: 36,
  },
  components: {
    Text: {
      base: { ...gtAmerica },
      default: { fontSize: 36 },
    },
    Title: {
      base: {
        ...gtAmerica,
        textTransform: 'uppercase',
        color: 'white',
      },
      default: {
        fontSize: 48,
      },
    },
    SemiTitle: {
      base: {
        ...gtAmerica,
        textTransform: 'uppercase',
        paddingVertical: 4,
      },
      default: { fontSize: 48 },
    },
  },
});
